import { HomeIcon } from "@heroicons/react/outline";
import { useMemo } from "react";

import { BreadcrumbItem } from "../types";

import { Folder } from "@/types/folders";

export const useFolderBreadcrumbs = (
  folders: Folder[],
  currentFolderId: number | null,
  workspaceId?: string,
  isFolderCurrentPage = true,
): BreadcrumbItem[] =>
  useMemo(() => {
    const items: BreadcrumbItem[] = [
      {
        label: "Home",
        href: `/workspace/${workspaceId}/home`,
        id: null,
        icon: <HomeIcon className="h-4 w-4" />,
      },
    ];

    if (!currentFolderId || !folders.length) {
      return items;
    }

    const buildPath = (folderId: number, visited = new Set<number>()): Folder[] => {
      // Prevent infinite loops
      if (visited.has(folderId)) {
        return [];
      }
      visited.add(folderId);

      const folder = folders.find((f: Folder) => f.id === folderId);
      if (!folder) {
        return [];
      }

      if (folder.parent_id) {
        const parentPath = buildPath(folder.parent_id, visited);
        return [...parentPath, folder];
      }

      return [folder];
    };

    const folderPath = buildPath(currentFolderId);

    folderPath.forEach((folder, index) => {
      const isLastFolder = index === folderPath.length - 1;
      items.push({
        label: folder.name,
        id: folder.id,
        href:
          isLastFolder && isFolderCurrentPage
            ? undefined // Current folder - no link
            : `/workspace/${workspaceId}/folder/${folder.id}`,
      });
    });

    return items;
  }, [folders, currentFolderId, workspaceId, isFolderCurrentPage]);
